// What? Adapters turning each view's section list into StructureNavNode trees.
// Why? StructureNavigator renders a tree, but neither source model is nested:
//      DocumentTypeSection encodes hierarchy via ParentSectionID only, and
//      GenerationSection encodes it via a numeric heading level.
// How? Pure functions, no React — see buildNavTree.test.ts.

import type {
  DocumentTypeSection,
  GenerationSection,
} from "../../../types/documentTypes";
import type { StructureNavNode } from "./types";

const FALLBACK_TITLE = "Untitled section";

const titleOf = (title?: string): string =>
  title && title.trim() ? title.trim() : FALLBACK_TITLE;

/**
 * Stable DOM id for a document-type section.
 *
 * Mirrors the draggableId fallback used by StructureTab so sections that have
 * not been persisted yet (no DocumentTypeSectionID) are still addressable.
 */
export const documentTypeSectionNavId = (
  section: DocumentTypeSection,
  index: number,
): string => String(section.DocumentTypeSectionID ?? `new-${index}`);

/**
 * Build a tree from the flat, depth-first ordered section list used by the
 * Document Structure editor. Hierarchy comes from ParentSectionID.
 *
 * Sections whose parent is missing from the list (orphans) are surfaced at the
 * root rather than dropped, so the navigator always accounts for every section.
 */
export const buildTreeFromDocumentTypeSections = (
  sections: DocumentTypeSection[],
): StructureNavNode[] => {
  if (!sections?.length) return [];

  const nodesById = new Map<string, StructureNavNode>();
  // Parent id is only meaningful for persisted sections, so key on the numeric id.
  const nodeBySectionId = new Map<number, StructureNavNode>();

  sections.forEach((section, index) => {
    const node: StructureNavNode = {
      id: documentTypeSectionNavId(section, index),
      title: titleOf(section.Title),
      children: [],
    };
    nodesById.set(node.id, node);
    if (section.DocumentTypeSectionID != null) {
      nodeBySectionId.set(section.DocumentTypeSectionID, node);
    }
  });

  const roots: StructureNavNode[] = [];

  sections.forEach((section, index) => {
    const node = nodesById.get(documentTypeSectionNavId(section, index));
    if (!node) return;

    const parentId = section.ParentSectionID;
    const parent = parentId != null ? nodeBySectionId.get(parentId) : undefined;

    // No parent, or a parent that isn't in this list (orphan) -> treat as root.
    if (!parent || parent === node) {
      roots.push(node);
      return;
    }
    parent.children.push(node);
  });

  return roots;
};

/**
 * Build a tree from generated document sections, which carry an explicit
 * heading level (1-based).
 *
 * Level jumps (e.g. an h1 followed by an h4) are clamped to one level deeper
 * than the current parent so a malformed document still yields a sane tree.
 */
export const buildTreeFromGenerationSections = (
  sections: GenerationSection[],
): StructureNavNode[] => {
  if (!sections?.length) return [];

  const roots: StructureNavNode[] = [];
  // Ancestors of the node being placed, index 0 being the outermost.
  const stack: StructureNavNode[] = [];

  sections.forEach((section) => {
    const node: StructureNavNode = {
      id: String(section.section_id),
      title: titleOf(section.title),
      children: [],
    };

    const rawLevel = section.level && section.level > 0 ? section.level : 1;
    // Clamp so we never skip a level: at most one deeper than the current stack.
    const depth = Math.min(rawLevel - 1, stack.length);

    stack.length = depth;

    if (depth === 0) {
      roots.push(node);
    } else {
      stack[depth - 1].children.push(node);
    }

    stack.push(node);
  });

  return roots;
};

/** Every id in the tree, in render (depth-first) order. */
export const flattenNavIds = (nodes: StructureNavNode[]): string[] => {
  const ids: string[] = [];
  const walk = (list: StructureNavNode[]) => {
    list.forEach((node) => {
      ids.push(node.id);
      walk(node.children);
    });
  };
  walk(nodes);
  return ids;
};

/** Ids of every node that has children — used to expand the tree by default. */
export const collectParentNavIds = (nodes: StructureNavNode[]): string[] => {
  const ids: string[] = [];
  const walk = (list: StructureNavNode[]) => {
    list.forEach((node) => {
      if (node.children.length) {
        ids.push(node.id);
        walk(node.children);
      }
    });
  };
  walk(nodes);
  return ids;
};
